import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  MapPin,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { UnallocatedBooking } from '@/hooks/useUnallocatedBookings';
import { BookingType, Resource, Booking, Project } from '@/types';
import { MatchScoreBadge } from '@/components/booking/MatchScoreBadge';
import { computeRecommendations } from '@/lib/recommendationEngine';
import { ResourceMatchResult } from '@/types/recommendation';
import { ColumnKey } from '@/pages/UnallocatedBookings';

interface UnallocatedTableRowProps {
  booking: UnallocatedBooking;
  bookingTypes: BookingType[];
  resources: Resource[];
  existingBookings: Booking[];
  projects: Project[];
  onAISuggest: (booking: UnallocatedBooking) => void;
  onViewInPlanner: (booking: UnallocatedBooking) => void;
  isSelected: boolean;
  onToggleSelect: (bookingId: string) => void;
  visibleColumns?: Set<ColumnKey>;
  columnOrder?: ColumnKey[];
}

function formatDateRange(startTime: string, endTime: string): string {
  const start = parseISO(startTime);
  const endRaw = parseISO(endTime);
  const endDisplay = new Date(endRaw);
  endDisplay.setDate(endDisplay.getDate() - 1);
  const sameDay =
    start.getFullYear() === endDisplay.getFullYear() &&
    start.getMonth() === endDisplay.getMonth() &&
    start.getDate() === endDisplay.getDate();
  if (sameDay) return format(start, 'd MMM yyyy');
  return `${format(start, 'd MMM')} – ${format(endDisplay, 'd MMM yy')}`;
}

// ─── Inline match cell ───────────────────────────────────────────────────────

interface InlineMatchCellProps {
  booking: UnallocatedBooking;
  resources: Resource[];
  existingBookings: Booking[];
  projects: Project[];
  onAISuggest: () => void;
}

function InlineMatchCell({ booking, resources, existingBookings, projects, onAISuggest }: InlineMatchCellProps) {
  const [topMatch, setTopMatch] = useState<ResourceMatchResult | null | 'loading'>('loading');
  const [totalMatches, setTotalMatches] = useState(0);

  const startDate = useMemo(() => parseISO(booking.startTime), [booking.startTime]);
  const endDate   = useMemo(() => parseISO(booking.endTime),   [booking.endTime]);

  useEffect(() => {
    setTopMatch('loading');
    const timer = setTimeout(() => {
      const output = computeRecommendations({
        startDate,
        endDate,
        requiredRole: booking.requiredRole,
        location: booking.location,
        requiredSkills: booking.requiredSkills ?? [],
        projectId: booking.projectId,
        projects,
        allResources: resources.filter(r => r.id !== 'unallocated'),
        existingBookings,
      });
      const best = output.cleanMatches[0] ?? output.conflictMatches[0] ?? null;
      setTopMatch(best);
      setTotalMatches(output.cleanMatches.length + output.conflictMatches.length);
    }, 120);
    return () => clearTimeout(timer);
  }, [booking, resources, existingBookings, startDate, endDate, projects]);

  if (topMatch === 'loading') {
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground/50">
        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
        <span className="text-xs">Scanning…</span>
      </div>
    );
  }

  if (topMatch === null) {
    return (
      <button
        onClick={onAISuggest}
        className="flex items-center gap-1.5 text-xs text-yellow-600 hover:text-yellow-700 transition-colors group"
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span className="group-hover:underline">No match</span>
      </button>
    );
  }

  const availIcon =
    topMatch.availabilityStatus === 'free'    ? <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />  :
    topMatch.availabilityStatus === 'partial' ? <Clock       className="h-3.5 w-3.5 text-amber-500 shrink-0" />  :
                                                <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onAISuggest}
            className="flex items-center gap-1.5 text-left group cursor-pointer focus-visible:outline-none w-full min-w-0"
          >
            {/* Name — fixed width so the score badge always aligns */}
            <span className="text-xs font-medium truncate w-[88px] shrink-0 group-hover:text-primary transition-colors">
              {topMatch.resource.name}
            </span>
            {availIcon}
            <MatchScoreBadge score={topMatch.overallScore} />
            {totalMatches > 1 && (
              <span className="text-xs text-muted-foreground/60 flex items-center gap-0.5 group-hover:text-primary transition-colors whitespace-nowrap ml-1">
                {totalMatches} matches
                <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform duration-150" />
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          {totalMatches} candidate{totalMatches !== 1 ? 's' : ''} found — click to review
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Row urgency styles ──────────────────────────────────────────────────────

const URGENCY_STRIPE: Record<string, string> = {
  critical: 'border-l-destructive',
  soon:     'border-l-yellow-400',
  upcoming: 'border-l-primary/30',
};

const URGENCY_DOT: Record<string, string> = {
  critical: 'bg-destructive animate-pulse',
  soon:     'bg-yellow-400',
  upcoming: 'bg-primary/40',
};

const URGENCY_LABEL: Record<string, { label: string; className: string }> = {
  critical: { label: 'Critical',  className: 'text-destructive font-semibold' },
  soon:     { label: 'Soon',      className: 'text-yellow-600 font-medium' },
  upcoming: { label: 'Upcoming',  className: 'text-muted-foreground' },
};

// ─── Main export ─────────────────────────────────────────────────────────────

export function UnallocatedTableRow({
  booking,
  bookingTypes,
  resources,
  existingBookings,
  projects,
  onAISuggest,
  onViewInPlanner,
  isSelected,
  onToggleSelect,
  visibleColumns,
  columnOrder,
}: UnallocatedTableRowProps) {
  const bookingType = bookingTypes.find(bt => String(bt.id) === String(booking.bookingTypeId));
  const dateRange   = formatDateRange(booking.startTime, booking.endTime);

  // Ordered list of column keys to render (fall back to default order if not provided)
  const orderedKeys: ColumnKey[] = columnOrder ?? ['urgency','booking','date','role','skills','location','type','bestMatch'];
  const isVisible = (key: ColumnKey) => !visibleColumns || visibleColumns.has(key);

  const cellForKey = (key: ColumnKey) => {
    switch (key) {
      case 'urgency': return (
        <TableCell key="urgency" className="w-24">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 cursor-default">
                  <span className={cn('h-2 w-2 rounded-full shrink-0', URGENCY_DOT[booking.urgency])} />
                  <span className={cn('text-xs', URGENCY_LABEL[booking.urgency].className)}>
                    {URGENCY_LABEL[booking.urgency].label}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {booking.daysUntilStart <= 0
                  ? 'Starts today or overdue'
                  : `Starts in ${booking.daysUntilStart} day${booking.daysUntilStart !== 1 ? 's' : ''}`}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      );
      case 'booking': return (
        <TableCell key="booking" className="w-[180px] max-w-[180px]">
          <TooltipProvider delayDuration={400}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <span className="text-sm font-semibold truncate block leading-snug w-full">
                    {booking.projectName}
                  </span>
                  {booking.customer && (
                    <span className="text-xs text-muted-foreground truncate block mt-0.5 w-full">
                      {booking.customer}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs max-w-[260px]">
                <p className="font-medium">{booking.projectName}</p>
                {booking.customer && <p className="text-muted-foreground mt-0.5">{booking.customer}</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      );
      case 'date': return (
        <TableCell key="date" className="w-[130px]">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <span className="tabular-nums">{dateRange}</span>
          </div>
        </TableCell>
      );
      case 'role': return (
        <TableCell key="role" className="w-[140px]">
          {booking.requiredRole ? (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 font-normal max-w-[130px] truncate block w-fit">
              {booking.requiredRole}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </TableCell>
      );
      case 'skills': return (
        <TableCell key="skills" className="w-[220px]">
          {booking.requiredSkills && booking.requiredSkills.length > 0 ? (
            <div className="flex items-center gap-1 flex-wrap">
              {booking.requiredSkills.slice(0, 3).map(skill => (
                <span
                  key={skill}
                  className="text-xs bg-muted/70 text-muted-foreground px-1.5 py-0.5 rounded whitespace-nowrap"
                >
                  {skill}
                </span>
              ))}
              {booking.requiredSkills.length > 3 && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground/60 cursor-default hover:text-muted-foreground transition-colors">
                        +{booking.requiredSkills.length - 3} more
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs max-w-[200px]">
                      <p className="font-medium mb-1 text-foreground">All skills</p>
                      {booking.requiredSkills.join(' · ')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </TableCell>
      );
      case 'location': return (
        <TableCell key="location" className="w-[110px]">
          {booking.location ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0 text-muted-foreground/50" />
              <span className="truncate">{booking.location}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </TableCell>
      );
      case 'type': return (
        <TableCell key="type" className="w-[90px]">
          {bookingType ? (
            <Badge variant="outline" className="text-xs px-1.5 py-0 font-normal w-fit">
              {bookingType.name}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </TableCell>
      );
      case 'bestMatch': return (
        <TableCell key="bestMatch" className="w-[280px]">
          <InlineMatchCell
            booking={booking}
            resources={resources}
            existingBookings={existingBookings}
            projects={projects}
            onAISuggest={() => onAISuggest(booking)}
          />
        </TableCell>
      );
      default: return null;
    }
  };

  return (
    <TableRow
      className={cn(
        'border-l-4 group transition-colors even:bg-muted/30',
        URGENCY_STRIPE[booking.urgency],
        isSelected && 'bg-primary/[0.03]',
      )}
    >
      {/* ── Select — always visible ── */}
      <TableCell className="w-8 pl-3 pr-0">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(booking.id)}
          aria-label={`Select ${booking.projectName}`}
        />
      </TableCell>

      {/* ── Ordered, visibility-filtered data cells ── */}
      {orderedKeys.filter(k => isVisible(k)).map(key => cellForKey(key))}

      {/* ── Actions — always visible ── */}
      <TableCell className="w-[70px] pr-3 text-right">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs gap-1 opacity-0 group-hover:opacity-100 transition-opacity focus-visible:opacity-100"
          onClick={() => onAISuggest(booking)}
        >
          Assign
        </Button>
      </TableCell>
    </TableRow>
  );
}
